/*  Shared  */
// import debug from 'application/Shared/Utils/Debug';
// import Env from 'application/Shared/Utils/Env';

/*  Helpers  */
import Helpers from './Functions';

const { isset } = Helpers;

class SessionHelper {
    constructor(session) {
        if (Helpers.isEmpty(session)) {
            this.session = {};
            return;
        }

        this.session = session;
    }

    exists() {
        const { session } = this;
        if (Helpers.isEmpty(session)) {
            return false;
        }
        return true;
    }

    get() {
        return this.session;
    }

    getAnmTenantReservedStatus() {
        const { session } = this;
        return session?.sessionSettings?.anmTenantReservedStatus;
    }

    getTenant() {
        const { session } = this;

        if (!isset(session, 'tenant')) {
            return false;
        }
        return session.tenant;
    }

    getTenantId() {
        const { session } = this;

        if (!isset(session, 'tenant')) {
            return false;
        }

        const { tenant } = session;
        return tenant?.id;
    }

    getTenantName() {
        const { session } = this;

        if (!isset(session, 'tenant')) {
            return false;
        }

        const { tenant } = session;
        return tenant?.name;
    }

    getInternalId() {
        const tenant = this.getTenant();

        if (!tenant) {
            return null;
        }

        return tenant.internalId;
    }

    getUserId() {
        if (!this.exists()) {
            return '';
        }

        const { session } = this;
        const { id } = session;
        return `${id}`;
    }

    getUser() {
        const { session } = this;

        if (!isset(session, 'user')) {
            return null;
        }

        return session.user;
    }

    getName() {
        if (!this.exists()) {
            return '';
        }

        const user = this.getUser();

        if (!user) {
            return '';
        }
        return [user.firstName, user.lastName].join(' ').trim();
    }

    getUsername() {
        if (!this.exists()) {
            return '';
        }

        const user = this.getUser();

        if (!user) {
            return '';
        }

        return user.email;
    }

    getTenantSettings() {
        const tenant = this.getTenant();

        if (!tenant?.settings) {
            return {};
        }
        return tenant.settings;
    }

    getTenantSetting(setting) {
        const settings = this.getTenantSettings();

        if (!isset(settings, setting)) {
            return false;
        }
        return settings[setting];
    }

    getSessionSettings() {
        const { session } = this;

        if (!isset(session, 'sessionSettings')) {
            return null;
        }
        return session.sessionSettings;
    }

    getPartnerTenantLinks() {
        const sessionSettings = this.getSessionSettings();

        if (!sessionSettings) {
            return [];
        }

        return sessionSettings?.partnerTenantLinks || [];
    }

    // commenting this code out for now as it may make a return in upcoming tickets.
    // /**
    //  * Builds an ANM login URL.
    //  *
    //  * The URL's params include:
    //  * * fromUri - URL that the user will be redirected to on successful login.
    //  * * auvikenv - used by Okta to ensure the user is sent to the proper env after login. Needed for daily/stage logins.
    //  *
    //  * Example complete URL: https://stage.auvik.com/auth/login?fromUri=https://mspdemo.stage.auvik.com/&auvikenv=stage
    //  * @returns {String}
    //  */
    // getAnmLoginUrlWithRedirect() {
    //     // calling 'new Env()' inside of the function so that it will be defined on function call and not globally,
    //     // to better support testing this function.
    //     const env = new Env();
    //     const anmBaseSiteUrl = env.get('REACT_APP_ANM_BASE_SITE_URL');

    //     const auvikEnv = env.get('REACT_APP_AUVIK_ENV');
    //     const auvikEnvParam = `${auvikEnv ? `&auvikenv=${auvikEnv}` : ''}`;

    //     const protocol = 'https://';

    //     const loginUrl = `${protocol}${anmBaseSiteUrl}/auth/login`;
    //     const defaultFromUriParam = `${protocol}${anmBaseSiteUrl}`;

    //     const defaultLoginUrlWithRedirect = `${loginUrl}?fromUri=${defaultFromUriParam}${auvikEnvParam}`;

    //     const tenant = this.getTenant();
    //     const identityTenantDomain = tenant?.identityTenantDomain;

    //     if (!this.exists() || !tenant || !identityTenantDomain) {
    //         return defaultLoginUrlWithRedirect;
    //     }

    //     const anmTenantUrl = `${protocol}${identityTenantDomain}.${anmBaseSiteUrl}`;
    //     // need to ensure the fromUri URL param is a valid URL for the redirect after login.
    //     try {
    //         // new URL() will return a URL object if anmTenantUrl is valid.
    //         // if anmTenantUrl is an invalid URL, it will throw an error.
    //         // eslint-disable-next-line no-unused-vars
    //         const parsedUrl = new URL(anmTenantUrl);
    //     } catch (e) {
    //         debug.e('Invalid redirect URL', anmTenantUrl, e.toString());
    //         return defaultLoginUrlWithRedirect;
    //     }

    //     return `${loginUrl}?fromUri=${anmTenantUrl}${auvikEnvParam}`;
    // }

    isGlobalAdmin() {
        const tenant = this.getTenant();

        if (!tenant) {
            return false;
        }
        return tenant.admin;
    }

    isANM() {
        const tenant = this.getTenant();

        if (!tenant) {
            return false;
        }

        if (tenant.identityTenantType === 'ANM') {
            return true;
        }

        return false;
    }

    isTrial() {
        const sessionSettings = this.getSessionSettings();

        return !!sessionSettings?.inTrial;
    }

    isLocked() {
        const { session } = this;
        return Helpers.isTrialExpired(session);
    }

    hasFeatureFlag(featureFlag) {
        return this.getTenantSetting(featureFlag);
    }

    hasLegacyInternal() {
        const tenant = this.getTenant();
        if (!tenant) {
            return false;
        }

        const { id, internalId } = tenant;
        return id === internalId;
    }

    hasPermission(REQUIRED, operator = 'and') {
        const { session } = this;

        if (!isset(session, 'user', 'permission', 'rules')) {
            return false;
        }

        // Make sure it's a supported operator.
        // If not, exit early.
        if (['and', 'or', 'none'].indexOf(operator) === -1) {
            return false;
        }

        let required = REQUIRED;
        if (!Array.isArray(required)) {
            required = [required];
        }

        let count = 0;

        required.forEach((permission) => {
            switch (permission) {
                case 'global_system_admin':
                    if (isset(session, 'tenant', 'admin')) {
                        count += session.tenant.admin ? 1 : 0;
                    }
                    break;
                case 'partner_dashboard_access':
                    if (
                        this.hasPermission('partner') &&
                        this.hasPermission(
                            ['system_admin', 'partner_sales_hub'],
                            'or'
                        )
                    ) {
                        count += 1;
                    }
                    break;
                case 'partner':
                    if (isset(session, 'tenant', 'partner')) {
                        count += session.tenant.partner ? 1 : 0;
                    }
                    break;
                case 'super_admin':
                    count = session.user.superAdmin ? 1 : 0;
                    break;
                default:
                    count +=
                        session.user.permission.rules.indexOf(permission) > -1
                            ? 1
                            : 0;
                    break;
            }
        });

        if (operator === 'none') {
            return count === 0;
        }

        if (operator === 'or') {
            return count > 0;
        }

        // In this case, operator === 'and', which is the default operator.
        return count === required.length;
    }

    isPartner() {
        if (
            this.hasPermission('partner') &&
            this.hasPermission(['system_admin', 'partner_sales_hub'], 'or')
        ) {
            return true;
        }

        return false;
    }

    hasInternalAccess() {
        return this.hasPermission('internal_deployment');
    }

    noInternalAccess() {
        return !this.hasPermission('internal_deployment');
    }
}

export default SessionHelper;
